import { useContext } from "react"
import PropTypes from "prop-types"
import { capitalize } from "lodash"
import { DESC } from "helpers/sorts"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { getPerformanceFilterInstrumentColumns } from "helpers/metrics"
import { ONE_DAY } from "helpers/dates"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import { useNormalPerformancePricingRefreshWarning } from "gatherers/hooks/use-normal-performance-pricing-refresh-warning"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { PERFORMANCE_CONTEXT } from "./context"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"

export default function AssetViewPerformance({ modelMetadata, context, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications }) {
  const { selectedMetricDisplay, selectedCategory } = useContext(PERFORMANCE_CONTEXT)
  const {
    instrument: original,
    performanceEndDate,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    performanceStartDate,
    period,
    isEndVsStartDateComparison,
    startDate,
    endDate,
  } = useContext(context)

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const apiStartDate = period === ONE_DAY ? performanceEndDate : performanceStartDate

  const gatherers = useTemplateGatherers(context, "performance", {
    selectedMetricDisplay,
    selectedCategory,
    classifications,
    from: apiStartDate,
    to: performanceEndDate,
  })

  useNormalPerformancePricingRefreshWarning({ gatherers, startDate, endDate })

  const metrics = useAssetViewDashboardMetrics(
    [{
      category: "performance",
      selectedMetricDisplay,
      selectedCategory,
      filterInstrumentColumns: getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
    }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: "assets.performance",
      downloadName: "Assets - Performance",
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      // Analytics callbacks
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      context,
    }}
  />
}

AssetViewPerformance.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  instrumentsWithQueryOptions: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
}
